import React, { useState, useEffect, useCallback } from 'react';
import { getActiveFiscalYearId } from '../../services/fiscalYearStorage';   // default to this tab's selected year
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, TextField, MenuItem, Button, CircularProgress, Card, CardContent,
  IconButton, Tooltip, Autocomplete, Chip, useTheme, Pagination, Fade,
} from '@mui/material';
import {
  Refresh as RefreshIcon, FileDownload as ExcelIcon, PictureAsPdf as PdfIcon,
  Print as PrintIcon, MenuBook, AccountBalance, TrendingUp, TrendingDown,
  FilterList, Search, Receipt, CalendarMonth, SwapHoriz,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { financialReportsService, GeneralLedgerFilter, AccountStatementResult } from '../../services/financialReports.service';
import { fiscalPeriodsService } from '../../services/fiscalPeriods.service';
import { chartOfAccountsService } from '../../services/chartOfAccounts.service';
import { companyInfoService } from '../../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';

// Types
interface FiscalPeriod { fiscalPeriodId: number; periodName: string; startDate: string; endDate: string; isCurrent: boolean; }
interface Account { accountId: number; accountCode: string; accountName: string; accountNameAr?: string; accountType?: string; accountNature?: string; isFinalAccount?: boolean; }
interface CompanyInfo { companyName?: string; companyNameAr?: string; logoBase64?: string | null; }

const translations = {
  en: {
    title: 'General Ledger', subtitle: 'Account Statement & Transaction History',
    selectAccount: 'Select Account', searchAccount: 'Search by code or name...',
    fiscalPeriod: 'Fiscal Period', selectPeriod: 'Select Period',
    fromDate: 'From Date', toDate: 'To Date', refresh: 'Load',
    date: 'Date', entryNo: 'Entry No.', description: 'Description',
    debit: 'Debit', credit: 'Credit', balance: 'Balance',
    openingBalance: 'Opening Balance', totalDebit: 'Total Debit', totalCredit: 'Total Credit',
    closingBalance: 'Closing Balance', netMovement: 'Net Movement',
    noData: 'Select an account and click "Load" to view transactions',
    noTransactions: 'No transactions found for the selected period',
    loading: 'Loading...', kwd: 'KWD', transactions: 'transactions',
    page: 'Page', of: 'of', rowsPerPage: 'Rows', accountInfo: 'Account',
    generatedAt: 'Generated at', all: 'All',
  },
  ar: {
    title: 'دفتر الأستاذ العام', subtitle: 'كشف حساب وسجل الحركات',
    selectAccount: 'اختر الحساب', searchAccount: 'ابحث بالكود أو الاسم...',
    fiscalPeriod: 'السنة المالية', selectPeriod: 'اختر السنة',
    fromDate: 'من تاريخ', toDate: 'إلى تاريخ', refresh: 'تحميل',
    date: 'التاريخ', entryNo: 'رقم القيد', description: 'البيان',
    debit: 'مدين', credit: 'دائن', balance: 'الرصيد',
    openingBalance: 'الرصيد الافتتاحي', totalDebit: 'إجمالي المدين', totalCredit: 'إجمالي الدائن',
    closingBalance: 'الرصيد الختامي', netMovement: 'صافي الحركة',
    noData: 'اختر حساب واضغط "تحميل" لعرض الحركات',
    noTransactions: 'لا توجد حركات للفترة المحددة',
    loading: 'جاري التحميل...', kwd: 'د.ك', transactions: 'حركة',
    page: 'صفحة', of: 'من', rowsPerPage: 'سطر', accountInfo: 'الحساب',
    generatedAt: 'تاريخ التقرير', all: 'الكل',
  },
};

const GeneralLedgerPage: React.FC = () => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const isDark = theme.palette.mode === 'dark';
  const { minDate, maxDate } = useFiscalDateRange();

  // State
  const [loading, setLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [reportData, setReportData] = useState<AccountStatementResult | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [fiscalPeriods, setFiscalPeriods] = useState<FiscalPeriod[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  // Filters
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [fiscalPeriodId, setFiscalPeriodId] = useState<number | undefined>();
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => { loadAccounts(); loadFiscalPeriods(); loadCompanyInfo(); }, []);

  const loadAccounts = async () => {
    setAccountsLoading(true);
    try {
      const data = await chartOfAccountsService.getAll();
      setAccounts(data.filter((a: Account) => a.isFinalAccount));
    } catch (error) { console.error('Error:', error); toast.error(isRTL ? 'خطأ في تحميل الحسابات' : 'Error loading accounts'); }
    finally { setAccountsLoading(false); }
  };

  const loadFiscalPeriods = async () => {
    try {
      const periods = await fiscalPeriodsService.getAll();
      setFiscalPeriods(periods);
      const current = (periods.find((p: FiscalPeriod) => String(p.fiscalPeriodId) === getActiveFiscalYearId()) ?? periods.find((p: FiscalPeriod) => p.isCurrent));
      if (current) setFiscalPeriodId(current.fiscalPeriodId);
    } catch (error) { console.error('Error:', error); }
  };

  const loadCompanyInfo = async () => {
    try { setCompanyInfo(await companyInfoService.getCompanyInfo()); } 
    catch (error) { console.error('Error:', error); }
  };

  const loadReport = useCallback(async () => {
    if (!selectedAccount) { toast.warning(isRTL ? 'اختر حساب أولاً' : 'Select an account first'); return; }
    setLoading(true);
    try {
      const filter: GeneralLedgerFilter = {
        accountId: selectedAccount.accountId, fiscalPeriodId,
        fromDate: fromDate || undefined, toDate: toDate || undefined,
        includeOpeningBalance: true, pageNumber, pageSize,
      };
      const result = await financialReportsService.getGeneralLedger(filter);
      setReportData(result);
      toast.success(`${result.transactions.length} ${t.transactions}`);
    } catch (error: any) { console.error('Error:', error); toast.error(error.response?.data?.message || 'Error'); }
    finally { setLoading(false); }
  }, [selectedAccount, fiscalPeriodId, fromDate, toDate, pageNumber, pageSize]);

  const formatNumber = (num: number) => num.toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  const totalPages = reportData ? Math.ceil(reportData.totalRecords / pageSize) : 0;

  // Export Excel
  const exportToExcel = async () => {
    if (!reportData) return;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(t.title);

    if (companyInfo?.logoBase64) {
      try { const img = wb.addImage({ base64: companyInfo.logoBase64, extension: 'png' }); ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 60, height: 60 } }); } catch (e) {}
    }

    ws.mergeCells('B1:F1'); ws.getCell('B1').value = companyInfo?.companyName || 'Company';
    ws.getCell('B1').font = { size: 14, bold: true }; ws.getCell('B1').alignment = { horizontal: 'center' };
    ws.mergeCells('B2:F2'); ws.getCell('B2').value = t.title;
    ws.getCell('B2').font = { size: 12, bold: true }; ws.getCell('B2').alignment = { horizontal: 'center' };
    ws.mergeCells('B3:F3'); ws.getCell('B3').value = `${reportData.accountCode} - ${isRTL ? reportData.accountNameAr : reportData.accountName}`;
    ws.getCell('B3').alignment = { horizontal: 'center' };
    ws.addRow([]);

    const opRow = ws.addRow([t.openingBalance, '', '', '', '', reportData.openingBalance]);
    opRow.eachCell(c => { c.font = { bold: true }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E3F2FD' } }; });

    const hRow = ws.addRow([t.date, t.entryNo, t.description, t.debit, t.credit, t.balance]);
    hRow.eachCell(c => {
      c.font = { bold: true, color: { argb: 'FFFFFF' } };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1976D2' } };
      c.alignment = { horizontal: 'center' };
      c.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
    });

    reportData.transactions.forEach(tx => {
      const r = ws.addRow([formatDate(tx.transactionDate), tx.journalEntryNumber, tx.description || '', tx.debitAmount, tx.creditAmount, tx.runningBalance]);
      r.eachCell((c, i) => { c.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }; if (i >= 4) c.numFmt = '#,##0.000'; });
    });

    const totRow = ws.addRow([t.closingBalance, '', '', reportData.totalDebit, reportData.totalCredit, reportData.closingBalance]);
    totRow.eachCell((c, i) => { c.font = { bold: true }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC107' } }; if (i >= 4) c.numFmt = '#,##0.000'; });

    ws.columns = [{ width: 12 }, { width: 14 }, { width: 40 }, { width: 14 }, { width: 14 }, { width: 16 }];

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `Ledger_${reportData.accountCode}_${new Date().toISOString().split('T')[0]}.xlsx`; a.click();
    toast.success('Excel exported');
  };

  // Export PDF
  const exportToPDF = () => {
    if (!reportData) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();

    if (companyInfo?.logoBase64) { try { doc.addImage(companyInfo.logoBase64, 'PNG', 10, 5, 20, 20); } catch (e) {} }

    doc.setFontSize(14); doc.text(companyInfo?.companyName || 'Company', pw / 2, 12, { align: 'center' });
    doc.setFontSize(12); doc.text(t.title, pw / 2, 19, { align: 'center' });
    doc.setFontSize(10); doc.text(`${reportData.accountCode} - ${isRTL ? reportData.accountNameAr : reportData.accountName}`, pw / 2, 26, { align: 'center' });
    doc.text(`${t.openingBalance}: ${formatNumber(reportData.openingBalance)} ${t.kwd}`, 14, 35);

    const body = reportData.transactions.map(tx => [formatDate(tx.transactionDate), tx.journalEntryNumber, tx.description || '', formatNumber(tx.debitAmount), formatNumber(tx.creditAmount), formatNumber(tx.runningBalance)]);
    body.push([t.closingBalance, '', '', formatNumber(reportData.totalDebit), formatNumber(reportData.totalCredit), formatNumber(reportData.closingBalance)]);

    autoTable(doc, {
      head: [[t.date, t.entryNo, t.description, t.debit, t.credit, t.balance]],
      body, startY: 40, styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [25, 118, 210], fontStyle: 'bold' },
      columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 22 }, 2: { cellWidth: 60 }, 3: { halign: 'right', cellWidth: 25 }, 4: { halign: 'right', cellWidth: 25 }, 5: { halign: 'right', cellWidth: 28 } },
      didParseCell: (data) => { if (data.row.index === reportData.transactions.length) { data.cell.styles.fillColor = [255, 193, 7]; data.cell.styles.fontStyle = 'bold'; } },
    });

    doc.save(`Ledger_${reportData.accountCode}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exported');
  };

  // Print
  const handlePrint = () => {
    if (!reportData) return;
    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head><title>${t.title}</title>
      <style>body{font-family:Arial;margin:20px;font-size:11px;}.header{text-align:center;margin-bottom:15px;}.header img{max-height:50px;margin-bottom:8px;}.header h1{margin:3px 0;font-size:16px;}.header h2{margin:3px 0;font-size:14px;color:#1976d2;}.summary{display:flex;justify-content:space-around;margin:15px 0;padding:10px;background:#e3f2fd;border-radius:5px;}.summary div{text-align:center;}.summary .lbl{font-size:10px;color:#666;}.summary .val{font-size:14px;font-weight:bold;color:#1976d2;}table{width:100%;border-collapse:collapse;margin-top:10px;}th,td{border:1px solid #ddd;padding:6px;text-align:${isRTL ? 'right' : 'left'};}th{background:#1976d2;color:#fff;font-size:10px;}.num{text-align:right;font-family:monospace;}.pos{color:#2e7d32;}.neg{color:#c62828;}.tot{background:#ffc107;font-weight:bold;}.opn{background:#e3f2fd;font-weight:bold;}@media print{body{margin:10px;}}</style></head>
      <body><div class="header">${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}"/>` : ''}<h1>${companyInfo?.companyName || ''}</h1><h2>${t.title}</h2><p>${reportData.accountCode} - ${isRTL ? reportData.accountNameAr : reportData.accountName}</p></div>
      <div class="summary"><div><div class="lbl">${t.openingBalance}</div><div class="val">${formatNumber(reportData.openingBalance)}</div></div><div><div class="lbl">${t.totalDebit}</div><div class="val pos">${formatNumber(reportData.totalDebit)}</div></div><div><div class="lbl">${t.totalCredit}</div><div class="val neg">${formatNumber(reportData.totalCredit)}</div></div><div><div class="lbl">${t.closingBalance}</div><div class="val">${formatNumber(reportData.closingBalance)}</div></div></div>
      <table><thead><tr><th>${t.date}</th><th>${t.entryNo}</th><th>${t.description}</th><th>${t.debit}</th><th>${t.credit}</th><th>${t.balance}</th></tr></thead><tbody>
      <tr class="opn"><td colspan="5">${t.openingBalance}</td><td class="num">${formatNumber(reportData.openingBalance)}</td></tr>
      ${reportData.transactions.map(tx => `<tr><td>${formatDate(tx.transactionDate)}</td><td>${tx.journalEntryNumber}</td><td>${tx.description || ''}</td><td class="num">${tx.debitAmount > 0 ? formatNumber(tx.debitAmount) : ''}</td><td class="num">${tx.creditAmount > 0 ? formatNumber(tx.creditAmount) : ''}</td><td class="num ${tx.runningBalance >= 0 ? 'pos' : 'neg'}">${formatNumber(tx.runningBalance)}</td></tr>`).join('')}
      <tr class="tot"><td colspan="3">${t.closingBalance}</td><td class="num">${formatNumber(reportData.totalDebit)}</td><td class="num">${formatNumber(reportData.totalCredit)}</td><td class="num">${formatNumber(reportData.closingBalance)}</td></tr>
      </tbody></table></body></html>`;
    const w = window.open('', '_blank'); if (w) { w.document.write(html); w.document.close(); setTimeout(() => { w.print(); w.close(); }, 400); }
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <MenuBook sx={{ fontSize: 36, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">{t.title}</Typography>
            <Typography variant="body2" color="textSecondary">{t.subtitle}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Excel"><IconButton onClick={exportToExcel} color="success" disabled={!reportData}><ExcelIcon /></IconButton></Tooltip>
          <Tooltip title="PDF"><IconButton onClick={exportToPDF} color="error" disabled={!reportData}><PdfIcon /></IconButton></Tooltip>
          <Tooltip title={t.refresh}><IconButton onClick={handlePrint} color="info" disabled={!reportData}><PrintIcon /></IconButton></Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterList color="action" />
          <Typography variant="subtitle1" fontWeight="medium">{t.selectAccount}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Autocomplete sx={{ minWidth: 350 }} options={accounts} loading={accountsLoading} value={selectedAccount}
            onChange={(_, v) => setSelectedAccount(v)}
            getOptionLabel={(o) => `${o.accountCode} - ${isRTL ? (o.accountNameAr || o.accountName) : o.accountName}`}
            renderOption={(props, o) => {
              const { key, ...rest } = props;
              return (
                <Box component="li" key={key} {...rest} sx={{ display: 'flex', gap: 1 }}>
                  <Chip label={o.accountCode} size="small" color="primary" variant="outlined" />
                  <Typography>{isRTL ? (o.accountNameAr || o.accountName) : o.accountName}</Typography>
                </Box>
              );
            }}
            renderInput={(params) => (<TextField {...params} size="small" label={t.selectAccount} placeholder={t.searchAccount}
              InputProps={{ ...params.InputProps, startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} /> }} />)}
          />
          <Autocomplete
            size="small"
            options={fiscalPeriods}
            getOptionLabel={(o) => o.periodName}
            value={fiscalPeriods.find(p => p.fiscalPeriodId === fiscalPeriodId) || null}
            onChange={(_, v) => setFiscalPeriodId(v?.fiscalPeriodId || undefined)}
            renderInput={(params) => <TextField {...params} label={t.fiscalPeriod} placeholder={t.selectPeriod} />}
            sx={{ minWidth: 200 }}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.fromDate}
              value={fromDate ? new Date(fromDate) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setFromDate(newValue);
                if (toDate && newValue && toDate < newValue) setToDate('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={minDate}
              maxDate={toDate ? new Date(toDate) : maxDate}
              slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.toDate}
              value={toDate ? new Date(toDate) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setToDate(newValue);
                if (fromDate && newValue && fromDate > newValue) setFromDate('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={fromDate ? new Date(fromDate) : minDate}
              maxDate={maxDate}
              slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
            />
          </LocalizationProvider>
          <TextField select size="small" label={t.rowsPerPage} sx={{ width: 90 }} value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPageNumber(1); }}>
            {[25, 50, 100, 200].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </TextField>
          <Button variant="contained" onClick={loadReport} startIcon={<RefreshIcon />} disabled={loading || !selectedAccount}>{t.refresh}</Button>
        </Box>
      </Paper>

      {/* Summary Cards */}
      {reportData && (
        <Fade in><Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Card sx={{ flex: '1 1 280px', bgcolor: isDark ? '#1e3a5f' : '#e3f2fd' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccountBalance color="primary" />
                <Typography variant="subtitle2" color="textSecondary">{t.accountInfo}</Typography>
              </Box>
              <Typography variant="h6" fontWeight="bold">{reportData.accountCode}</Typography>
              <Typography>{isRTL ? reportData.accountNameAr : reportData.accountName}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 160px' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="textSecondary">{t.openingBalance}</Typography>
              <Typography variant="h5" fontWeight="bold" color="info.main">{formatNumber(reportData.openingBalance)}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 160px', bgcolor: isDark ? '#1b4332' : '#e8f5e9' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <TrendingUp sx={{ color: 'success.main', fontSize: 20 }} />
                <Typography variant="caption" color="textSecondary">{t.totalDebit}</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" color="success.main">{formatNumber(reportData.totalDebit)}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 160px', bgcolor: isDark ? '#4a1515' : '#ffebee' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <TrendingDown sx={{ color: 'error.main', fontSize: 20 }} />
                <Typography variant="caption" color="textSecondary">{t.totalCredit}</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" color="error.main">{formatNumber(reportData.totalCredit)}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 160px', bgcolor: isDark ? '#4a3800' : '#fff8e1' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="textSecondary">{t.closingBalance}</Typography>
              <Typography variant="h5" fontWeight="bold" color={reportData.closingBalance >= 0 ? 'success.main' : 'error.main'}>
                {formatNumber(reportData.closingBalance)}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 120px' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <SwapHoriz color="secondary" />
              <Typography variant="caption" color="textSecondary">{t.netMovement}</Typography>
              <Typography variant="h6" fontWeight="bold" color="secondary">{formatNumber(reportData.totalDebit - reportData.totalCredit)}</Typography>
            </CardContent>
          </Card>
        </Box></Fade>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
        ) : !reportData ? (
          <Box sx={{ textAlign: 'center', p: 5 }}>
            <Receipt sx={{ fontSize: 60, color: 'action.disabled', mb: 2 }} />
            <Typography color="textSecondary">{t.noData}</Typography>
          </Box>
        ) : reportData.transactions.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 5 }}>
            <CalendarMonth sx={{ fontSize: 60, color: 'action.disabled', mb: 2 }} />
            <Typography color="textSecondary">{t.noTransactions}</Typography>
          </Box>
        ) : (
          <>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', width: 100 }}>{t.date}</TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', width: 120 }}>{t.entryNo}</TableCell>
                  <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>{t.description}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: 'success.dark', color: 'white', fontWeight: 'bold', width: 130 }}>{t.debit}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: 'error.dark', color: 'white', fontWeight: 'bold', width: 130 }}>{t.credit}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: 'warning.dark', color: 'white', fontWeight: 'bold', width: 140 }}>{t.balance}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow sx={{ bgcolor: isDark ? '#1e3a5f' : '#e3f2fd' }}>
                  <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>{t.openingBalance}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(reportData.openingBalance)}</TableCell>
                </TableRow>
                {reportData.transactions.map((tx) => (
                  <TableRow key={tx.ledgerId} hover sx={{ '&:nth-of-type(odd)': { bgcolor: isDark ? '#1a1a1a' : '#fafafa' } }}>
                    <TableCell>{formatDate(tx.transactionDate)}</TableCell>
                    <TableCell><Chip label={tx.journalEntryNumber} size="small" variant="outlined" color="primary" /></TableCell>
                    <TableCell>{tx.description || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: tx.debitAmount > 0 ? 'success.main' : 'text.disabled' }}>
                      {tx.debitAmount > 0 ? formatNumber(tx.debitAmount) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: tx.creditAmount > 0 ? 'error.main' : 'text.disabled' }}>
                      {tx.creditAmount > 0 ? formatNumber(tx.creditAmount) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: tx.runningBalance >= 0 ? 'success.main' : 'error.main' }}>
                      {formatNumber(tx.runningBalance)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: '#ffc107' }}>
                  <TableCell colSpan={3} sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>{t.closingBalance}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(reportData.totalDebit)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{formatNumber(reportData.totalCredit)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '1rem' }}>{formatNumber(reportData.closingBalance)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <Pagination count={totalPages} page={pageNumber} onChange={(_, p) => { setPageNumber(p); loadReport(); }} color="primary" showFirstButton showLastButton />
              </Box>
            )}
          </>
        )}
      </TableContainer>

      {reportData && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', color: 'text.secondary' }}>
          <Typography variant="caption">{reportData.totalRecords} {t.transactions} | {t.page} {pageNumber} {t.of} {totalPages || 1}</Typography>
          <Typography variant="caption">{t.generatedAt}: {new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default GeneralLedgerPage;